import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Link
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  AccountBalance as AccountBalanceIcon,
  AccountBalanceWallet as WalletIcon,
  CloudUpload as CloudUploadIcon,
  HourglassEmpty as HourglassIcon
} from '@mui/icons-material';
import { fetchJson, resolveApiUrl } from '../../../utils/fetchJson';

const STEPS = ['Review Invoice', 'Payment Details', 'Confirmation'];

// Offline channel is always offered (bank transfer / mobile money) so schools can
// pay and submit a proof even when no online gateway is configured.
const OFFLINE_METHOD = {
  value: 'offline',
  label: 'Bank / Mobile Money',
  img: null,
  color: '#2E7D32',
  offline: true
};

export default function SubscriptionRenewalPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Billing info from backend
  const [billing, setBilling] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [latestInvoice, setLatestInvoice] = useState(null);
  const [balance, setBalance] = useState(null);
  const [latestPayment, setLatestPayment] = useState(null);

  // Available payment methods from system configuration
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Offline payment: platform instructions + the school's own submissions
  const [instructions, setInstructions] = useState(null);
  const [myOfflinePayments, setMyOfflinePayments] = useState([]);

  // Payment form
  const [planId, setPlanId] = useState('');
  const [provider, setProvider] = useState('');
  const [msisdn, setMsisdn] = useState('');
  const [email, setEmail] = useState('');
  const [payerName, setPayerName] = useState('');

  // Offline-specific form fields
  const [offReference, setOffReference] = useState('');
  const [offNotes, setOffNotes] = useState('');
  const [offFile, setOffFile] = useState(null);

  const PROVIDER_META = {
    mtn_momo:     { label: 'MTN MoMo',      img: '/Mtn-momo.png',      color: '#FFCC00' },
    orange_money: { label: 'Orange Money',  img: '/orange-money.png',  color: '#FF6600' },
    stripe:       { label: 'Bank Card',     img: '/logo192.png',       color: '#635BFF' },
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [subResp, plansResp, methodsResp, instrResp, myPayResp] = await Promise.all([
        fetchJson('/api/subscriptions/me', { method: 'GET' }),
        fetchJson('/api/subscriptions/plans', { method: 'GET' }),
        fetchJson('/api/subscriptions/payment-methods', { method: 'GET' }).catch(() => ({ methods: [] })),
        fetchJson('/api/subscriptions/payment-instructions', { method: 'GET' }).catch(() => ({ instructions: null })),
        fetchJson('/api/subscriptions/my-offline-payments', { method: 'GET' }).catch(() => ({ payments: [] }))
      ]);

      // Subscription & billing
      setSubscription(subResp?.subscription || null);
      setBilling(subResp?.billing || null);
      setLatestInvoice(subResp?.latestInvoice || null);
      setBalance(subResp?.balance || null);
      setLatestPayment(subResp?.latestPayment || null);

      // Plans
      const list = Array.isArray(plansResp?.plans) ? plansResp.plans : (Array.isArray(plansResp) ? plansResp : []);
      setPlans(list);

      // Auto-select the school's current plan or first available
      const currentPlanId = subResp?.subscription?.planId;
      if (currentPlanId && list.find(p => p.id === currentPlanId)) {
        setPlanId(String(currentPlanId));
      } else if (list.length) {
        setPlanId(String(list[0].id));
      }

      // Payment instructions + the school's own offline submissions
      setInstructions(instrResp?.instructions || null);
      setMyOfflinePayments(Array.isArray(myPayResp?.payments) ? myPayResp.payments : []);

      // Payment methods: gateways (if configured) plus the always-available offline channel
      const rawMethods = Array.isArray(methodsResp?.methods) ? methodsResp.methods : [];
      const gateways = rawMethods.map(m => ({
        value: m.id,
        label: PROVIDER_META[m.id]?.label || m.label || m.id,
        img:   PROVIDER_META[m.id]?.img   || '/logo192.png',
        color: PROVIDER_META[m.id]?.color  || '#888888',
        requiresPhone: !!m.requiresPhone,
      }));
      const available = [...gateways, OFFLINE_METHOD];
      setPaymentMethods(available);
      // Prefer an online gateway when present, else default to offline
      setProvider(gateways.length ? gateways[0].value : OFFLINE_METHOD.value);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const selectedPlan = plans.find(p => String(p.id) === String(planId));
  const totalAmount = billing?.totalAmount || 0;
  const currency = billing?.currency || 'XAF';
  const isOffline = provider === 'offline';

  // The most recent offline submission (to show its review status)
  const latestOffline = myOfflinePayments.length ? myOfflinePayments[0] : null;

  const submit = async () => {
    setSubmitting(true);
    setError('');
    setResult(null);
    try {
      const resp = await fetchJson('/api/subscriptions/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: Number(planId),
          provider,
          msisdn: msisdn || undefined,
          email: email || undefined,
          payerName: payerName || undefined
        })
      });
      setResult(resp);
      setActiveStep(2);
      if (resp?.redirectUrl) {
        setTimeout(() => { window.location.href = resp.redirectUrl; }, 2000);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitOffline = async () => {
    if (!offFile) {
      setError('Please attach a proof of payment (screenshot or PDF).');
      return;
    }
    setSubmitting(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('amount', String(totalAmount));
      if (payerName) fd.append('payerName', payerName);
      if (offReference) fd.append('reference', offReference);
      if (offNotes) fd.append('notes', offNotes);
      fd.append('proof', offFile);

      const resp = await fetchJson('/api/subscriptions/submit-offline-payment', {
        method: 'POST',
        body: fd
      });
      setResult({ ...resp, offline: true, amount: totalAmount, currency, reference: offReference });
      setActiveStep(2);
      // Refresh the school's submissions list
      await fetchJson('/api/subscriptions/my-offline-payments', { method: 'GET' })
        .then(r => setMyOfflinePayments(Array.isArray(r?.payments) ? r.payments : []))
        .catch(() => {});
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return 'success';
    if (s === 'grace' || s === 'trial') return 'warning';
    if (s === 'expired' || s === 'suspended') return 'error';
    return 'default';
  };

  // Map an offline payment status to a display label + color
  const offlineStatusView = (p) => {
    const s = (p?.status || '').toLowerCase();
    if (s === 'completed') return { label: 'Approved', color: 'success' };
    if (s === 'cancelled' || s === 'failed') return { label: 'Rejected', color: 'error' };
    return { label: 'Awaiting review', color: 'warning' };
  };

  const hasInstructions = instructions && Object.values(instructions).some(v => String(v || '').trim());

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Subscription Renewal</Typography>
          <Typography variant="body2" color="text.secondary">
            {billing?.schoolName || 'Your School'} • {billing?.academicYearName || 'Current Academic Year'}
          </Typography>
        </Box>
        {subscription?.status && (
          <Chip
            label={subscription.status}
            color={getStatusColor(subscription.status)}
            sx={{ fontWeight: 700, textTransform: 'capitalize', fontSize: '0.85rem' }}
          />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Latest offline submission status (so schools can track review / resubmit) */}
      {latestOffline && activeStep === 0 && (
        <Alert
          severity={offlineStatusView(latestOffline).color === 'success' ? 'success' : offlineStatusView(latestOffline).color === 'error' ? 'error' : 'info'}
          icon={offlineStatusView(latestOffline).color === 'warning' ? <HourglassIcon /> : undefined}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">
            Your last submitted payment of <strong>{Number(latestOffline.amount || 0).toLocaleString()} {currency}</strong> is <strong>{offlineStatusView(latestOffline).label}</strong>.
          </Typography>
          {(latestOffline.status === 'cancelled' || latestOffline.status === 'failed') && latestOffline.failureReason && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Reason: {latestOffline.failureReason}. You may submit a corrected payment below.
            </Typography>
          )}
          {latestOffline.proofUrl && (
            <Link href={resolveApiUrl(latestOffline.proofUrl)} target="_blank" rel="noopener" sx={{ fontSize: '0.8rem' }}>
              View submitted proof
            </Link>
          )}
        </Alert>
      )}

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 0: Review Invoice */}
      {activeStep === 0 && (
        <>
          {/* Invoice Summary Card */}
          <Card sx={{ mb: 3, overflow: 'visible' }}>
            <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 3, textAlign: 'center', borderRadius: '12px 12px 0 0' }}>
              <ReceiptIcon sx={{ fontSize: 40, mb: 1, opacity: 0.85 }} />
              <Typography variant="h3" fontWeight={800}>
                {Number(totalAmount).toLocaleString()} <Typography component="span" variant="h6">{currency}</Typography>
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                Total Subscription Fee
              </Typography>
            </Box>
            <CardContent>
              {/* Billing Breakdown */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <PeopleIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>{billing?.studentCount || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Students</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <AccountBalanceIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>{Number(billing?.pricePerStudent || 0).toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary">{currency}/Student</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <CalendarIcon color="primary" />
                    <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                      {selectedPlan?.billingCycle || subscription?.SubscriptionPlan?.billingCycle || 'Annual'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Billing Cycle</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <SchoolIcon color="primary" />
                    <Typography variant="h6" fontWeight={700}>
                      {selectedPlan?.name || subscription?.SubscriptionPlan?.name || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Plan</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Billing Period */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Billing Period</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {billing?.periodStart ? new Date(billing.periodStart).toLocaleDateString() : '-'} — {billing?.periodEnd ? new Date(billing.periodEnd).toLocaleDateString() : '-'}
                </Typography>
              </Box>

              {/* Current Subscription Dates */}
              {subscription && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Current Subscription</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {subscription.startDate ? new Date(subscription.startDate).toLocaleDateString() : '-'} — {subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : '-'}
                  </Typography>
                </Box>
              )}

              {/* Pricing formula */}
              <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mt: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Pricing Formula</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {billing?.studentCount || 0} students × {Number(billing?.pricePerStudent || 0).toLocaleString()} {currency} = <strong>{Number(totalAmount).toLocaleString()} {currency}</strong>
                </Typography>
              </Box>

              {/* Outstanding balance from being activated on a part payment. This is
                  money still owed by a deadline, so it leads the section. */}
              {balance && Number(balance.outstanding) > 0 && (
                <Alert severity={balance.overdue ? 'error' : 'warning'} sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Outstanding balance: {Number(balance.outstanding).toLocaleString()} {balance.currency || currency}
                  </Typography>
                  <Typography variant="body2">
                    {balance.overdue
                      ? <>This balance was due on <strong>{new Date(balance.dueDate).toLocaleDateString()}</strong> and is now overdue. Please settle it to avoid interruption of service.</>
                      : <>To be paid by <strong>{balance.dueDate ? new Date(balance.dueDate).toLocaleDateString() : '—'}</strong>. Your subscription is active in the meantime.</>}
                  </Typography>
                </Alert>
              )}

              {/* Status of the last receipt this school submitted. */}
              {latestPayment && (
                <Alert
                  severity={
                    latestPayment.status === 'completed' ? 'success'
                      : latestPayment.status === 'pending' ? 'info'
                        : 'error'
                  }
                  sx={{ mt: 2 }}
                >
                  {latestPayment.status === 'pending' && (
                    <>Your payment of <strong>{Number(latestPayment.amount).toLocaleString()} {currency}</strong> submitted on{' '}
                      {new Date(latestPayment.submittedAt).toLocaleDateString()} is awaiting approval.</>
                  )}
                  {latestPayment.status === 'completed' && (
                    <>Your payment of <strong>{Number(latestPayment.amount).toLocaleString()} {currency}</strong> was approved
                      {latestPayment.reviewedAt ? ` on ${new Date(latestPayment.reviewedAt).toLocaleDateString()}` : ''}.</>
                  )}
                  {latestPayment.status !== 'pending' && latestPayment.status !== 'completed' && (
                    <>Your payment of <strong>{Number(latestPayment.amount).toLocaleString()} {currency}</strong> was not accepted
                      {latestPayment.reason ? <>: <em>{latestPayment.reason}</em></> : '.'}</>
                  )}
                </Alert>
              )}

              {/* Latest Invoice Warning */}
              {latestInvoice && latestInvoice.status !== 'paid' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  You have an unpaid invoice <strong>#{latestInvoice.invoiceNumber}</strong> for <strong>{Number(latestInvoice.amount || 0).toLocaleString()} {currency}</strong> (status: {latestInvoice.status}). Proceeding will create a new invoice.
                </Alert>
              )}

              {latestInvoice && latestInvoice.status === 'paid' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Your latest invoice <strong>#{latestInvoice.invoiceNumber}</strong> is fully paid.
                </Alert>
              )}

              {/* Plan selection */}
              {plans.length > 1 && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Select Plan</InputLabel>
                  <Select label="Select Plan" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                    {plans.map(p => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <Typography>{p.name}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{p.billingCycle}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {selectedPlan?.description && (
                <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                  <Typography variant="body2">{selectedPlan.description}</Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setActiveStep(1)}
              disabled={!planId || totalAmount <= 0}
              endIcon={<PaymentIcon />}
            >
              Proceed to Payment
            </Button>
          </Box>
        </>
      )}

      {/* Step 1: Payment Details */}
      {activeStep === 1 && (
        <>
          {/* Amount Reminder */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>Payment Details</Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Amount Due</Typography>
                  <Typography variant="h5" fontWeight={800} color="primary.main">
                    {Number(totalAmount).toLocaleString()} {currency}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Payment Method */}
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Choose Payment Method</Typography>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {paymentMethods.map(pm => (
                  <Grid item xs={4} key={pm.value}>
                    <Box
                      onClick={() => setProvider(pm.value)}
                      sx={{
                        border: '2px solid',
                        borderColor: provider === pm.value ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        bgcolor: provider === pm.value ? 'primary.50' : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: 'primary.light', bgcolor: 'grey.50' }
                      }}
                    >
                      {pm.offline ? (
                        <WalletIcon sx={{ fontSize: 40, mb: 0.5, color: pm.color }} />
                      ) : (
                        <Box
                          component="img"
                          src={pm.img}
                          alt={pm.label}
                          sx={{ width: 40, height: 40, objectFit: 'contain', mb: 0.5 }}
                        />
                      )}
                      <Typography variant="caption" display="block" fontWeight={provider === pm.value ? 700 : 400}>
                        {pm.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Payer Info (shared) */}
              <TextField
                label="Payer Name"
                fullWidth
                sx={{ mb: 2 }}
                value={payerName}
                onChange={(e) => setPayerName(e.target.value)}
                placeholder="Full name of the person making payment"
              />

              {/* Online gateway fields */}
              {!isOffline && paymentMethods.find(m => m.value === provider)?.requiresPhone && (
                <TextField
                  label="Phone Number (MSISDN)"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={msisdn}
                  onChange={(e) => setMsisdn(e.target.value)}
                  placeholder="e.g. 237670000000"
                  helperText="Enter the phone number to receive the payment prompt"
                />
              )}

              {!isOffline && provider === 'stripe' && (
                <TextField
                  label="Email Address"
                  type="email"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  helperText="A payment link will be sent to this email"
                />
              )}

              {/* Offline channel: instructions + proof submission */}
              {isOffline && (
                <>
                  <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceIcon fontSize="small" /> Where to Pay
                    </Typography>
                    {hasInstructions ? (
                      <Grid container spacing={1}>
                        {[
                          ['Bank', instructions.bankName],
                          ['Account Name', instructions.accountName],
                          ['Account Number', instructions.accountNumber],
                          ['Branch', instructions.bankBranch],
                          ['SWIFT/BIC', instructions.swift],
                          ['MTN MoMo', instructions.mtnNumber && `${instructions.mtnNumber}${instructions.mtnName ? ` (${instructions.mtnName})` : ''}`],
                          ['Orange Money', instructions.orangeNumber && `${instructions.orangeNumber}${instructions.orangeName ? ` (${instructions.orangeName})` : ''}`],
                        ].filter(([, v]) => String(v || '').trim()).map(([k, v]) => (
                          <Grid item xs={12} sm={6} key={k}>
                            <Typography variant="caption" color="text.secondary" display="block">{k}</Typography>
                            <Typography variant="body2" fontWeight={600}>{v}</Typography>
                          </Grid>
                        ))}
                        {String(instructions.instructions || '').trim() && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" display="block">Notes</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{instructions.instructions}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Payment instructions have not been configured yet. Please contact the platform administrator for the bank / mobile money details before submitting.
                      </Typography>
                    )}
                  </Box>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    After paying via the channel above, enter the transaction reference and attach a proof (screenshot or PDF). An administrator will verify it and activate your renewal.
                  </Alert>

                  <TextField
                    label="Transaction Reference"
                    fullWidth
                    sx={{ mb: 2 }}
                    value={offReference}
                    onChange={(e) => setOffReference(e.target.value)}
                    placeholder="e.g. MoMo/bank transaction ID"
                  />
                  <TextField
                    label="Notes (optional)"
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mb: 2 }}
                    value={offNotes}
                    onChange={(e) => setOffNotes(e.target.value)}
                    placeholder="Anything the administrator should know"
                  />

                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    {offFile ? 'Change Proof File' : 'Attach Proof of Payment'}
                    <input
                      type="file"
                      hidden
                      accept="image/*,application/pdf"
                      onChange={(e) => setOffFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {offFile && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                      Selected: {offFile.name}
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={() => setActiveStep(0)}>
              Back
            </Button>
            {isOffline ? (
              <Button
                variant="contained"
                size="large"
                color="primary"
                onClick={submitOffline}
                disabled={submitting || !planId || totalAmount <= 0 || !offFile}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
              >
                {submitting ? 'Submitting...' : 'Submit Payment for Review'}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                color="primary"
                onClick={submit}
                disabled={submitting || !planId || !provider || (paymentMethods.find(m => m.value === provider)?.requiresPhone && !msisdn) || (provider === 'stripe' && !email)}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <PaymentIcon />}
              >
                {submitting ? 'Processing...' : `Pay ${Number(totalAmount).toLocaleString()} ${currency}`}
              </Button>
            )}
          </Box>
        </>
      )}

      {/* Step 2: Confirmation */}
      {activeStep === 2 && result && (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            {result.offline ? (
              <HourglassIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            ) : (
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            )}
            <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
              {result.offline ? 'Payment Submitted for Review' : 'Payment Initiated Successfully'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {result.offline ? (
                <>Your payment of <strong>{Number(result.amount || 0).toLocaleString()} {result.currency || currency}</strong> has been submitted. An administrator will verify your proof and activate your renewal.</>
              ) : (
                <>Your subscription payment of <strong>{Number(result.amount || 0).toLocaleString()} {result.currency || currency}</strong> has been submitted.</>
              )}
            </Typography>

            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, maxWidth: 400, mx: 'auto', mb: 3 }}>
              {result.invoiceId && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Invoice</Typography>
                  <Typography variant="body2" fontWeight={600}>#{result.invoiceId}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Amount</Typography>
                <Typography variant="body2" fontWeight={600}>{Number(result.amount || 0).toLocaleString()} {result.currency || currency}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Method</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                  {result.offline ? 'Bank / Mobile Money' : (result.provider || '').replace(/_/g, ' ')}
                </Typography>
              </Box>
              {result.reference && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Reference</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{result.reference}</Typography>
                </Box>
              )}
            </Box>

            {result.offline && (
              <Alert severity="info" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
                Status: Awaiting administrator review. You can track it on this page.
              </Alert>
            )}

            {result.redirectUrl && (
              <Alert severity="info" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
                Redirecting to payment page...
              </Alert>
            )}

            {!result.offline && provider === 'mtn_momo' && (
              <Alert severity="info" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
                A payment prompt has been sent to your phone. Please approve the transaction on your MTN MoMo app.
              </Alert>
            )}

            {!result.offline && provider === 'orange_money' && (
              <Alert severity="info" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
                Your Orange Money payment is being processed. You will receive a confirmation SMS shortly.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="outlined" onClick={() => { setResult(null); setActiveStep(0); setOffFile(null); setOffReference(''); setOffNotes(''); loadData(); }}>
                Back to Overview
              </Button>
              <Button variant="contained" onClick={loadData}>
                Refresh Status
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
